import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSystemSettings } from '../contexts/SystemSettingsContext';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CampaignIcon from '@mui/icons-material/Campaign';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HotelIcon from '@mui/icons-material/Hotel';
import SpaIcon from '@mui/icons-material/Spa';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { getOrders } from '../services/orderService';
import { Order } from '../types/order';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessDate } from '../hooks/useBusinessDate';
import { parse, addDays, setHours, setMinutes, setSeconds } from 'date-fns';
import { toCastOrderView, extractOrders } from '../features/cast/castOrderUtils';
import { toast } from 'react-toastify';
import RichTextEditor from '../components/common/RichTextEditor';
import { updateDashboardNotice } from '../services/settings';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { businessDate, isLoading: isDateLoading, formatBusinessDate, businessHours } = useBusinessDate();
  const { loading: settingsLoading, settings, refreshSettings } = useSystemSettings();

  // キャスト本人には自分の名前表示は不要なため、非キャスト（管理者・ドライバー等）のみキャスト名を表示する
  const isCast = String(user?.role || '').toUpperCase() === 'CAST';

  // 連絡事項の編集権限（管理者系：ADMIN / MANAGER のみ）
  const canEditNotice = ['ADMIN', 'MANAGER'].includes(String(user?.role || '').toUpperCase());
  // キャストには連絡事項を表示しない（管理者系・ドライバー向け）
  const showNotice = !isCast;

  const [noticeEditing, setNoticeEditing] = useState(false);
  const [noticeDraft, setNoticeDraft] = useState('');
  const [noticeSaving, setNoticeSaving] = useState(false);

  const notice = settings?.dashboard_notice || '';

  const handleStartEditNotice = () => {
    setNoticeDraft(notice);
    setNoticeEditing(true);
  };

  const handleCancelEditNotice = () => {
    setNoticeEditing(false);
    setNoticeDraft('');
  };

  const handleSaveNotice = async () => {
    setNoticeSaving(true);
    try {
      await updateDashboardNotice(noticeDraft);
      await refreshSettings();
      setNoticeEditing(false);
      toast.success('連絡事項を更新しました');
    } catch (err: any) {
      console.error('連絡事項の更新に失敗:', err);
      toast.error(err?.response?.data?.error || '連絡事項の更新に失敗しました');
    } finally {
      setNoticeSaving(false);
    }
  };

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // 予約時間の並び順（asc=早い順 / desc=遅い順）
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // 予約時間でソートした表示用オーダー（view）一覧
  const sortedViews = useMemo(() => {
    const views = orders.map((o: any) => toCastOrderView(o));
    views.sort((a, b) =>
      sortOrder === 'asc'
        ? a.reservationSortKey - b.reservationSortKey
        : b.reservationSortKey - a.reservationSortKey
    );
    return views;
  }, [orders, sortOrder]);

  // オーダー情報を取得する
  const fetchOrders = useCallback(async (date: Date) => {
    if (!user || !businessHours) return;

    try {
      setLoading(true);
      setError(null);
      
      const selectedDate = date;
      const startTime = parse(businessHours.start, 'HH:mm', new Date());
      const endTime = parse(businessHours.end, 'HH:mm', new Date());

      let searchStart = setSeconds(setMinutes(setHours(selectedDate, startTime.getHours()), startTime.getMinutes()), 0);
      let searchEnd;

      if (endTime < startTime) {
        const nextDay = addDays(selectedDate, 1);
        searchEnd = setSeconds(setMinutes(setHours(nextDay, endTime.getHours()), endTime.getMinutes()), 0);
      } else {
        searchEnd = setSeconds(setMinutes(setHours(selectedDate, endTime.getHours()), endTime.getMinutes()), 0);
      }

      // user_id は明示指定しない。
      // バックエンドがログインユーザーのロールに応じて自動で絞り込む
      // (CAST=自分が担当のオーダー / DRIVER=自分が担当 / 管理者=全体)。
      const response = await getOrders({
        start_date: searchStart.toISOString(),
        end_date: searchEnd.toISOString(),
      });
      
      setOrders(extractOrders(response));

    } catch (err) {
      setError('オーダーデータの取得に失敗しました。');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user, businessHours]);

  // 初期化処理は不要になったため削除

  useEffect(() => {
    const init = async () => {
      if (user && businessDate && !settingsLoading && !isDateLoading) {
        await fetchOrders(businessDate);
      } else if (!settingsLoading && !isDateLoading) {
        // データ取得の前提条件が整っていない場合も、ローディングを終了させる
        setLoading(false);
      }
    };
    init();
  }, [user, businessDate, settingsLoading, isDateLoading, fetchOrders]);

  // 営業時間情報を表示
  const renderBusinessHours = () => {
    if (!businessHours) {
      return <Typography>営業時間情報が取得できませんでした</Typography>;
    }
    
    return (
      <Typography>
        営業時間: {businessHours.start} - {businessHours.end}
      </Typography>
    );
  };

  // 営業時間設定(businessHours)や営業日(businessDate)が未取得のうちは
  // デフォルト値が表示されてしまうため、揃うまでローディング表示にする。
  if (loading || settingsLoading || isDateLoading || !businessHours || !businessDate) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        ダッシュボード
      </Typography>

      {showNotice && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CampaignIcon color="primary" />
              <Typography variant="h6">連絡事項</Typography>
            </Stack>
            {canEditNotice && !noticeEditing && (
              <Button size="small" startIcon={<EditIcon />} onClick={handleStartEditNotice}>
                編集
              </Button>
            )}
          </Stack>

          {noticeEditing ? (
            <Box>
              <RichTextEditor value={noticeDraft} onChange={setNoticeDraft} />
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
                <Button onClick={handleCancelEditNotice} disabled={noticeSaving}>
                  キャンセル
                </Button>
                <Button variant="contained" onClick={handleSaveNotice} disabled={noticeSaving}>
                  {noticeSaving ? '保存中...' : '保存'}
                </Button>
              </Stack>
            </Box>
          ) : notice ? (
            <Box
              sx={{
                '& ul, & ol': { pl: 3, my: 1 },
                '& h3': { my: 1 },
                '& p': { my: 0.5 },
                fontSize: 14,
                lineHeight: 1.7,
                wordBreak: 'break-word',
              }}
              dangerouslySetInnerHTML={{ __html: notice }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              連絡事項はありません
            </Typography>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">営業日: {formatBusinessDate(businessDate)}</Typography>
            {renderBusinessHours()}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6">オーダー数: {orders.length}</Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}
      
      <Paper sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          sx={{ mb: 1, gap: 1 }}
        >
          <Typography variant="h6">
            本日のオーダー一覧
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sortOrder}
            onChange={(_e, val) => val && setSortOrder(val)}
          >
            <ToggleButton value="asc">予約が早い順</ToggleButton>
            <ToggleButton value="desc">予約が遅い順</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {sortedViews.length === 0 ? (
          <Typography>オーダーがありません</Typography>
        ) : (
          // 全ロール共通: 予約時間・予約タイプ・コース・ホテルをカードにまとめて表示し、
          // タップで該当オーダーの編集画面(/orders/:id)へ遷移する。
          <Stack spacing={1.5}>
            {sortedViews.map((view: any) => {
              return (
                <Card key={view.id} variant="outlined">
                  <CardActionArea onClick={() => navigate(`/orders/${view.id}`)}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {view.reservationLabel}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {view.isCompleted && (
                            <Chip label="完了" size="small" color="success" />
                          )}
                          <Chip label={view.orderTypeLabel} size="small" color="primary" variant="outlined" />
                        </Stack>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                        <SpaIcon fontSize="small" color="action" />
                        <Typography variant="body1">{view.courseLabel}</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <HotelIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {view.hotelLabel}
                        </Typography>
                      </Stack>
                      {/* 完了オーダーは入室・退出時間を表示する */}
                      {view.isCompleted && (view.entryTimeLabel || view.exitTimeLabel) && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                          <AccessTimeIcon fontSize="small" color="success" />
                          <Typography variant="body2" color="success.main">
                            入室 {view.entryTimeLabel || '―'}
                            {view.exitTimeLabel ? ` / 退出 ${view.exitTimeLabel}` : ''}
                          </Typography>
                        </Stack>
                      )}
                      {/* キャスト以外（管理者・ドライバー等）には担当キャスト名を表示 */}
                      {!isCast && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                          <PersonIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {view.castNameLabel}
                          </Typography>
                        </Stack>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default Dashboard;
